import React, { useCallback, useContext, useMemo } from "react";
import { AuthContext, IntlContext } from "context";
import { InfiniteListContext } from "context/InfiniteList/Context";
import { InfiniteListArea } from "components/List";
import { DebouncedKeywordSearchForm, KeywordNoResults } from "components/ListPage";
import ComponentList from "./ComponentList";
import CardSkeleton from "./CardSkeleton";
import SelectSort from "app/Booking/List/SelectSort";
import { ServiceInterface } from "types/service";
import useQueryString from "helpers/useQueryString";
import { Card } from "components/ui/card";
import FiltersBar from "./FiltersBar";
import {
    buildResetInlineProviderFilterSearchParams,
    buildSearchParams,
    getSingleQueryValue,
    hasActiveInlineProviderFilters,
} from "./searchParams";
import type { SortOption } from "app/Booking/List/SelectSort";

const ServiceGridSkeleton: React.FC<{ count: number }> = ({ count }) => (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: count }).map((_, i) => (
            <CardSkeleton key={i} />
        ))}
    </div>
);

const OurServicesList: React.FC = () => {
    const intl = useContext(IntlContext);
    const { isLoggedIn } = useContext(AuthContext);
    const { pager, activeFilters, changeFetcherParams, requestedPerPage } = useContext(InfiniteListContext);
    const { values: queryValues, setAll } = useQueryString();
    const currentKeyword = getSingleQueryValue(queryValues.filter_keyword ?? activeFilters.filter_keyword).trim();
    const hasKeyword = currentKeyword !== "";
    const hasInlineProviderFilters = hasActiveInlineProviderFilters({ ...activeFilters, ...queryValues });

    const sortOptions = useMemo<SortOption[]>(
        () => [
            { field: "price", order: "asc", label: intl.formatMessage("our_services.order.price.asc") },
            { field: "price", order: "desc", label: intl.formatMessage("our_services.order.price.desc") },
            {
                field: "created_at",
                order: "desc",
                label: intl.formatMessage("our_services.order.date.desc"),
            },
            { field: "created_at", order: "asc", label: intl.formatMessage("our_services.order.date.asc") },
            { field: "rating", order: "desc", label: intl.formatMessage("our_services.order.rating.asc") },
            {
                field: "provider_created_at",
                order: "asc",
                label: intl.formatMessage("our_services.order.provider.asc"),
            },
        ],
        [intl],
    );

    const handleKeywordChange = useCallback(
        (keyword: string) => {
            const params = buildSearchParams({
                activeFilters: activeFilters as HashMap<unknown>,
                keyword,
                pager,
            });

            changeFetcherParams(params);
            setAll(params);
        },
        [activeFilters, changeFetcherParams, pager, setAll],
    );

    const handleKeywordReset = useCallback(() => {
        handleKeywordChange("");
    }, [handleKeywordChange]);

    const handleFiltersReset = useCallback(() => {
        const params = buildResetInlineProviderFilterSearchParams({
            currentQueryValues: queryValues,
            pager,
        });

        changeFetcherParams(params);
        setAll(params);
    }, [changeFetcherParams, pager, queryValues, setAll]);

    const inlineNoResults = (hasKeyword || hasInlineProviderFilters) ? (
        <KeywordNoResults
            message={String(
                hasKeyword
                    ? intl.formatMessage("providers.list.search_no_results", { keyword: currentKeyword })
                    : intl.formatMessage("providers.list.filters.no_results"),
            )}
            resetLabel={String(
                intl.formatMessage(hasKeyword ? "providers.list.search_reset" : "providers.list.filters.reset")
            )}
            onReset={hasKeyword ? handleKeywordReset : handleFiltersReset}
        />
    ) : undefined;

    return (
        <React.Fragment>
            <Card className="rounded-3xl p-4 sm:p-6">
                <div className="flex flex-col gap-3">
                    <DebouncedKeywordSearchForm
                        currentKeyword={currentKeyword}
                        placeholder={String(intl.formatMessage("providers.list.search_placeholder"))}
                        onDebouncedChange={handleKeywordChange}
                    />
                    <FiltersBar />
                </div>
            </Card>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-muted-foreground">
                    {intl.formatMessage("providers.list.results_count", { count: pager.total })}
                </div>
                <SelectSort
                    sortOptions={sortOptions}
                    withIconSort={true}
                    pager={pager}
                    activeFilters={activeFilters as HashMap<unknown>}
                    onChangeFetcherParams={changeFetcherParams as (params: HashMap<unknown>) => void}
                />
            </div>

            <div className="mt-6">
                <InfiniteListArea
                    skeleton={<ServiceGridSkeleton count={requestedPerPage} />}
                    emptyContent={inlineNoResults}
                    labels={{
                        errorTitle: intl.formatMessage("providers.list.error_title"),
                        errorDescription: intl.formatMessage("providers.list.error_description"),
                        retry: intl.formatMessage("providers.list.retry"),
                        emptyTitle: intl.formatMessage("providers.list.empty_title"),
                        emptyDescription: intl.formatMessage("providers.list.empty_description"),
                        resetFilters: intl.formatMessage("providers.list.reset_filters"),
                        ...(isLoggedIn
                            ? { endOfList: intl.formatMessage("providers.list.end_of_list") }
                            : {}),
                    }}
                >
                    {(items: ServiceInterface[]) => <ComponentList items={items} />}
                </InfiniteListArea>
            </div>
        </React.Fragment>
    );
};

export default OurServicesList;
